































































































































































































/* globals process */
import { EventBus } from '@/event-bus';
import { FormValidation, OpenHelp } from '@/mixins';
import { required, minLength, numeric, requiredIf } from 'vuelidate/lib/validators';
import { getContentArea } from '@/contentful';
import {
  OpenForgotUsernameScreen,
  OpenForgotPasswordScreen,
} from '@/mixins/get-content-mixins';
import { FooterSecondary } from '@/components/global-components/global-footer-component/footer-components';
import { ExtendMixins } from '@/services';
import {
  MobileMenuTray,
} from '@/components/global-components/global-header-component/header-components';
import { Auth } from '@aws-amplify/auth';
import { Hub } from '@aws-amplify/core';
import { AuthService } from '@/services';
import Cookies from 'js-cookie';

interface Data {
  credentials: {
    username: string;
    password: string;
    MFACode: string;
  };
  MFAChallenged: boolean;
  MFAChallengeResponse: any;
  resendCodeTimeRemaining: string;
  rememberDevice: boolean;
  clientName: string;
  showLoggedOutMessage: boolean;
  passwordHidden: boolean;
  error: string | null;
  warning: string;
  loading: boolean;
  sessionTimeout: boolean;
  loginContent: string;
  learnLink: string;
  signedIn: boolean;
}

export default ExtendMixins(
  OpenForgotUsernameScreen,
  OpenForgotPasswordScreen,
  FormValidation,
).extend({
  name: 'LoginPage',
  beforeRouteEnter(to, from, next) {
    getContentArea([
      'fraudulent-login-error',
      'login-error-invalid-credentials',
      'login-content',
      'login-error-disabled',
      'login-error-locked',
      'login-error-password-reset',
    ]).then((content) => {
      if (content) {
        if (content[0] && content[0].fields) {
          to.meta.fraudError = content[0].fields.content;
        }
        if (content[1] && content[1].fields) {
          to.meta.credentialsError = content[1].fields.content;
        }
        if (content[2] && content[2].fields) {
          to.meta.loginContent = content[2].fields.content;
        }
        if (content[3] && content[3].fields) {
          to.meta.loginDisabled = content[3].fields.content;
        }
        if (content[4] && content[4].fields) {
          to.meta.loginLocked = content[4].fields.content;
        }
        if (content[5] && content[5].fields) {
          to.meta.passwordResetError = content[5].fields.content;
        }
      }
      next();
    });
  },
  data(): Data {
    return {
      credentials: {
        username: '',
        password: '',
        MFACode: '',
      },
      MFAChallenged: false,
      MFAChallengeResponse: null,
      resendCodeTimeRemaining: '',
      rememberDevice: false,
      clientName: process.env.VUE_APP_CLIENT_NAME,
      showLoggedOutMessage: false,
      passwordHidden: true,
      error: null,
      warning: '',
      loading: false,
      sessionTimeout: true,
      loginContent: this.$route.meta.loginContent,
      learnLink: '',
      signedIn: true,
    };
  },
  beforeCreate() {
    Hub.listen('auth', (data) => {
      const { payload } = data;
      if (payload.event === 'cognitoHostedUI') {
        Auth.currentAuthenticatedUser().then((user) => {
          const token = user.signInUserSession.idToken.jwtToken;
          const refreshToken = user.signInUserSession.refreshToken.token;
          AuthService.setCookie(token);
          Cookies.set('refreshToken', refreshToken, {
            secure: true,
          });
          Cookies.set('federatedSignIn', true);
        }).then(() => {
          const redirectUri = encodeURIComponent(`${window.location.origin}/portal/accounts`);
          AuthService.clearCognitoLocalStorage();
          window.location.href = `https://${process.env.VUE_APP_COGNITO_OAUTH_URL}/logout?client_id=${Auth.configure().userPoolWebClientId}&logout_uri=${redirectUri}`;
        });
      }
    });
  },
  beforeMount() {
    this.constructShowErrorModel('credentials');
    getContentArea(['global-learn-link']).then((data) => {
      const contentAreas = data;
      if (contentAreas) {
        if (contentAreas[0] && contentAreas[0].fields) {
          this.learnLink = contentAreas[0].fields.actionValue;
        }
      }
    });
  },
  mounted() {
    if (this.$route.params.launchModal === 'forgot-password') {
      this.forgotPassword();
    } else if (this.$route.params.launchModal === 'forgot-username') {
      this.forgotUsername();
    }
  },
  validations: {
    credentials: {
      username: {
        required,
      },
      password: {
        required,
        minLength: minLength(8),
      },
      MFACode: {
        required: requiredIf(function (this: any) {
          return this.MFAChallenged;
        }),
        numeric,
        minLength: minLength(6),
      },
    },
  },
  methods: {
    openMobileMenu() {
      EventBus.$emit('openMobileMenu');
    },
    contactURL(): string {
      if (this.clientName === 'Vermont') {
        return this.$options.filters.t('login_contact-vermont-url');
      } else if (process.env.VUE_APP_CONTACT_URL) {
        return process.env.VUE_APP_CONTACT_URL;
      }

      return this.$options.filters.t('login_contact-url');
    },
    isRoute(action): boolean {
      return action ? action.indexOf('route="') > -1 : false;
    },
    isLink(action): boolean {
      return action ? action.indexOf('link="') > -1 : false;
    },
    isSelfLink(action): boolean {
      return action ? action.indexOf('link:self="') > -1 : false;
    },
    constructRoute(action): string {
      let regex;
      if (this.isRoute(action)) {
        regex = /(route=")(.*)(")/g;
      } else if (this.isLink(action)) {
        regex = /(link=")(.*)(")/g;
      } else if (this.isSelfLink(action)) {
        regex = /(link:self=")(.*)(")/g;
      }
      return action.replace(regex, (match, a, b) => {
        return b;
      });
    },
    forgotUsername(): void {
      this.openForgotUsernameScreen({});
    },
    forgotPassword(): void {
      this.openForgotPasswordScreen({});
    },
    login(): void {
      this.error = '';
      this.warning = '';
      this.loading = true;
      let storeAction;
      let actionParams;

      if (this.MFAChallenged) {
        storeAction = 'respondToMFAChallenge';
        actionParams = {
          MFACode: this.credentials.MFACode,
          challengeResponse: this.MFAChallengeResponse,
          rememberDevice: this.rememberDevice,
        };
      } else {
        storeAction = 'loginUser';
        this.credentials.username = this.credentials.username.trim();
        this.credentials.password = this.credentials.password.trim();
        actionParams = {
          username: this.credentials.username,
          password: this.credentials.password,
        };
      }
      this.$store
        .dispatch(storeAction, actionParams)
        .then((res) => {
          if (res && res.message === 'mfa-challenge') {
            this.MFAChallenged = true;
            this.MFAChallengeResponse = res.response;
            this.resendCodeTimeRemaining = ' (in 180s)';
            let timeLeft = 180;
            const interval = setInterval(() => {
              timeLeft -= 1;
              this.resendCodeTimeRemaining = ` (in ${timeLeft}s)`;
              if (timeLeft <= 0) {
                this.resendCodeTimeRemaining = '';
                clearInterval(interval);
              }
            }, 1000);
            this.warning = this.$options.filters
              ? this.$options.filters.t('login-signin-box-mfa-warning')
              : '';
            this.loading = false;
            return;
          }
          // GTM Event - Login
          window.dataLayer.push({
            event: 'memberLogin',
          });
          if (this.$route.params.routeAfterLogin) {
            this.$router.replace({
              name: this.$route.params.routeAfterLogin,
            });
          } else {
              this.$router.replace({
                name: 'accounts',
              });
          }
        })
        .catch((error) => {
          if (error.message === 'PreAuthentication failed with error Member identified as fraudulent.') {
            this.error = this.$route.meta.fraudError;
          } else if (error.message === 'User is disabled.') {
            this.error = this.$route.meta.loginDisabled;
          } else if (error.message === 'Password attempts exceeded') {
            this.error = this.$route.meta.loginLocked;
          } else if (error.message === 'New password required') {
            this.error = this.$route.meta.passwordResetError;
          } else if (error.name === 'CodeMismatchException') {
            this.error = this.$options.filters!.t('login-signin-box-code-mismatch-error');
          } else if (this.MFAChallenged && this.resendCodeTimeRemaining === '') {
            this.error = this.$options.filters!.t('login-signin-box-code-expired-error');
          } else {
            this.error = this.$route.meta.credentialsError;
          }
          this.loading = false;
        });
    },
    register(): void {
      this.$router.push({ name: 'register' });
    },
    resendCode(): void {
      this.MFAChallenged = false;
      this.credentials.MFACode = '';
      this.login();
    },
  },
  computed: {
    hasActiveHSA(): boolean {
      return this.$store.getters.hasActiveHSA;
    },
    usernameErrors(): string[] {
      const formField = 'credentials.username';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'login_form-error_username_required',
            'global_form-error_username_required',
            'global_form-error_default_required',
          ],
          'required',
        ),
      ]);
    },
    passwordErrors(): string[] {
      const formField = 'credentials.password';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          [
            'login_form-error_password_required',
            'global_form-error_password_required',
            'global_form-error_default_required',
          ],
          'required',
        ),
        this.FormValidationMixin.validateField(
          formField,
          ['login_form-hint_password'],
          'minLength',
        ),
      ]);
    },
    MFACodeErrors(): string[] {
      const formField = 'credentials.MFACode';
      return this.FormValidationMixin.constructErrors(formField, [
        this.FormValidationMixin.validateField(
          formField,
          ['login-mfa-error-required'],
          'required',
        ),
        this.FormValidationMixin.validateField(
          formField,
          ['login-mfa-error-invalid'],
          'numeric',
        ),
        this.FormValidationMixin.validateField(
          formField,
          ['login-mfa-error-invalid-length'],
          'minLength',
        ),
      ]);
    },
  },
  components: {
    FooterSecondary,
    MobileMenuTray,
  },
});
